/* ProductInventory — track owned skincare products, low-stock warnings. */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, Package, AlertTriangle, ChevronDown, ChevronRight, Loader2, MinusCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductInventory() {
  const [items, setItems] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showList, setShowList] = useState(false);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', category: 'kram', uses_per_week: 7 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: list }, { data: cats }] = await Promise.all([
        axios.get(`${API}/skincare/inventory`, { withCredentials: true }),
        axios.get(`${API}/skincare/inventory/categories`, { withCredentials: true }),
      ]);
      setItems(list.items || []);
      setLowStock(list.low_stock || []);
      setCategories(cats.categories || []);
    } catch {
      toast.error('Kunde inte ladda inventariet');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addProduct = async () => {
    if (form.name.trim().length < 2) {
      toast.error('Ge produkten ett namn');
      return;
    }
    try {
      await axios.post(`${API}/skincare/inventory`, form, { withCredentials: true });
      setForm({ name: '', category: 'kram', uses_per_week: 7 });
      setAdding(false);
      toast.success('Produkt tillagd 💜');
      await load();
    } catch { toast.error('Kunde inte spara'); }
  };

  const use = async (id) => {
    try {
      await axios.post(`${API}/skincare/inventory/${id}/use`, {}, { withCredentials: true });
      await load();
    } catch { toast.error('Kunde inte uppdatera'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Ta bort denna produkt?')) return;
    try {
      await axios.delete(`${API}/skincare/inventory/${id}`, { withCredentials: true });
      toast.success('Borttagen');
      await load();
    } catch { toast.error('Kunde inte ta bort'); }
  };

  const setPct = async (id, pct) => {
    try {
      await axios.patch(`${API}/skincare/inventory/${id}`, { remaining_pct: pct }, { withCredentials: true });
      await load();
    } catch { toast.error('Kunde inte spara'); }
  };

  const catLabel = (cid) => (categories.find((c) => c.id === cid) || {}).label || cid;
  const catEmoji = (cid) => (categories.find((c) => c.id === cid) || {}).emoji || '🎀';

  if (loading) {
    return (
      <div className="rounded-2xl bg-white border border-rose-100 p-4 text-center text-slate-400 text-sm">
        <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Laddar produkter…
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-white border border-rose-100" data-testid="skincare-inventory">
      <button
        type="button"
        onClick={() => setShowList((v) => !v)}
        className="w-full flex items-center justify-between p-3"
        data-testid="skincare-inventory-toggle"
      >
        <div className="flex items-center gap-2">
          <Package className="w-4 h-4 text-rose-500" />
          <div className="text-sm font-semibold text-slate-800">Produkt-inventarie</div>
          <span className="text-[11px] text-slate-400">({items.length})</span>
          {lowStock.length > 0 && (
            <span className="text-[10px] rounded-full px-1.5 py-0.5 bg-amber-100 border border-amber-300 text-amber-800 inline-flex items-center gap-0.5">
              <AlertTriangle className="w-2.5 h-2.5" /> {lowStock.length} nästan slut
            </span>
          )}
        </div>
        {showList ? <ChevronDown className="w-3.5 h-3.5 text-slate-500" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-500" />}
      </button>

      {/* Low-stock always-visible warning */}
      {lowStock.length > 0 && !showList && (
        <div className="px-3 pb-3">
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-2 text-xs text-amber-900">
            <b>Nästan slut:</b> {lowStock.map((l) => l.name).join(', ')}
          </div>
        </div>
      )}

      {showList && (
        <div className="p-3 pt-0 space-y-2">
          {items.length === 0 && !adding && (
            <div className="text-xs text-slate-400 italic text-center py-3">
              Inga produkter än — lägg till dina hudvårds-favoriter för att hålla koll på när de tar slut.
            </div>
          )}

          {items.map((it) => {
            const pct = it.remaining_pct;
            const barColor = pct < 20 ? 'bg-red-400' : pct < 50 ? 'bg-amber-400' : 'bg-emerald-400';
            return (
              <div
                key={it.id}
                className={`rounded-xl p-2.5 border ${it.low_stock ? 'bg-amber-50/60 border-amber-200' : 'bg-white border-slate-200'}`}
                data-testid={`inventory-item-${it.id}`}
              >
                <div className="flex items-start gap-2">
                  <div className="text-xl leading-none flex-shrink-0 mt-0.5">{catEmoji(it.category)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <div className="text-sm font-semibold text-slate-900 truncate">{it.name}</div>
                      <span className="text-[10px] rounded-full px-1.5 py-0.5 bg-rose-50 border border-rose-200 text-rose-800">
                        {catLabel(it.category)}
                      </span>
                      {it.low_stock && (
                        <span className="text-[10px] rounded-full px-1.5 py-0.5 bg-amber-100 border border-amber-300 text-amber-900 inline-flex items-center gap-0.5">
                          <AlertTriangle className="w-2.5 h-2.5" /> köp mer
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-full ${barColor}`} style={{ width: `${pct}%`, transition: 'width 0.4s ease' }} />
                      </div>
                      <div className="text-[10px] text-slate-600 w-9 text-right tabular-nums">{pct}%</div>
                    </div>
                    <div className="flex items-center gap-1.5 mt-1.5">
                      <button
                        onClick={() => use(it.id)}
                        data-testid={`inventory-use-${it.id}`}
                        className="text-[10px] px-2 py-0.5 rounded-full bg-rose-100 hover:bg-rose-200 text-rose-800 inline-flex items-center gap-1"
                      >
                        <MinusCircle className="w-2.5 h-2.5" /> använd
                      </button>
                      {[100, 75, 50, 25].map((v) => (
                        <button
                          key={v}
                          onClick={() => setPct(it.id, v)}
                          className={`text-[10px] px-1.5 py-0.5 rounded-full border ${
                            Math.abs(pct - v) < 8 ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'
                          }`}
                        >
                          {v}%
                        </button>
                      ))}
                    </div>
                  </div>
                  <button
                    onClick={() => remove(it.id)}
                    className="text-slate-300 hover:text-red-500 flex-shrink-0"
                    aria-label="Ta bort"
                    data-testid={`inventory-delete-${it.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}

          {adding ? (
            <div className="rounded-xl bg-rose-50 border border-rose-200 p-3 space-y-2">
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Produktnamn (t.ex. CeraVe Moisturizing Cream)"
                data-testid="inventory-input-name"
                className="w-full text-sm rounded-lg border border-slate-200 px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-rose-200"
                autoFocus
              />
              <div className="flex gap-1.5 flex-wrap">
                {categories.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setForm({ ...form, category: c.id })}
                    data-testid={`inventory-cat-${c.id}`}
                    className={`text-[11px] px-2 py-0.5 rounded-full border ${
                      form.category === c.id ? 'bg-rose-500 text-white border-rose-500' : 'bg-white border-slate-200 text-slate-600'
                    }`}
                  >
                    {c.emoji} {c.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 text-xs">
                <label className="text-slate-600">Ungefär ggr/vecka:</label>
                <input
                  type="number"
                  min="1"
                  max="21"
                  value={form.uses_per_week}
                  onChange={(e) => setForm({ ...form, uses_per_week: parseInt(e.target.value, 10) || 7 })}
                  className="w-16 text-xs rounded-lg border border-slate-200 px-2 py-1 text-center"
                />
              </div>
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => { setAdding(false); setForm({ name: '', category: 'kram', uses_per_week: 7 }); }}
                  className="text-xs px-3 py-1 rounded-full bg-white border border-slate-200 text-slate-600"
                >
                  Avbryt
                </button>
                <button
                  onClick={addProduct}
                  data-testid="inventory-save"
                  className="text-xs px-3 py-1 rounded-full bg-rose-500 hover:bg-rose-600 text-white font-semibold"
                >
                  Spara
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setAdding(true)}
              data-testid="inventory-add-btn"
              className="w-full text-xs px-3 py-2 rounded-xl bg-rose-50 hover:bg-rose-100 border border-dashed border-rose-300 text-rose-800 inline-flex items-center justify-center gap-1.5"
            >
              <Plus className="w-3.5 h-3.5" /> Lägg till produkt
            </button>
          )}
        </div>
      )}
    </div>
  );
}
